import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { mainApiUrl, storeId as storeIdConstant, userTimezone } from '@constants'
import { base62Encode } from '@src/utils'
import { prepareHeaders, transformResponse } from '@src/reduxjs'
import ms from 'ms'

const albumsApi = createApi({
  reducerPath: 'albumsApi',
  baseQuery: fetchBaseQuery({ baseUrl: mainApiUrl, prepareHeaders }),
  tagTypes: ['Albums', 'LabelsForAlbums'],
  endpoints: (builder) => ({
    getLabelsForAlbum: builder.query({
      query: (albumId) => {
        return {
          url: `albums/${albumId}/labels`,
          method: 'GET',
        }
      },
      providesTags: (results, error, albumId) => {
        return [{ type: 'LabelsForAlbums', id: albumId }]
      },
      transformResponse,
    }),
    getJobsForAlbum: builder.query({
      query: (album) => {
        return {
          url: `albums/${album.id}/jobs`,
          method: 'GET',
        }
      },
      transformResponse,
    }),
    getPhotographersForAlbum: builder.query({
      query: ({ album, startDay, startTime, startMinute, isStore }) => {
        const params = { timezone: base62Encode(userTimezone) }

        if (startDay && startDay.value) {
          const hours = (startTime || {}).value || 0
          const minutes = (startMinute || {}).value || 0

          params['start_at'] = startDay.value + hours * ms('1h') + minutes * ms('1m')
        }

        if (isStore) {
          params.is_store = isStore
        }

        return { url: `albums/${album.id}/photographers`, params, method: 'GET' }
      },
      transformResponse,
    }),
    getStoreAlbums: builder.query({
      query: ({ storeId, page, perPage, showHidden, dates, timezone, isStore } = {}) => {
        const params = {}

        if (page) {
          params.page = page
        }

        if (perPage) {
          params.perPage = perPage
        }

        if (showHidden) {
          params.show_hidden = showHidden
        }

        if (dates) {
          params.dates = dates
        }

        if (isStore) {
          params.is_store = isStore
        }

        if (timezone) {
          params.timezone = base62Encode(timezone)
        }

        return { url: `stores/${storeId || storeIdConstant}/albums`, params }
      },
      providesTags: (data) => {
        return [ ...(data || []).map((i) => ({ type: 'Albums', id: i.id })), { type: 'Albums', id: 'LIST' } ]
      },
      transformResponse,
    }),
    createAlbum: builder.mutation({
      query: (data) => {
        return {
          url: `albums`,
          method: 'POST',
          body: data,
        }
      },
      invalidatesTags: (results, error, data) => {
        return [{ type: 'Albums', id: data.id }]
      },
    }),
    updateAlbum: builder.mutation({
      query: (data) => {
        return {
          url: `albums/${data.id}`,
          method: 'PATCH',
          body: data,
        }
      },
      invalidatesTags: (results, error, data) => {
        return [{ type: 'Albums', id: data.id }]
      },
    }),
    deleteAlbum: builder.mutation({
      query: ({ id }) => {
        return {
          url: `albums/${id}`,
          method: 'DELETE',
        }
      },
      invalidatesTags: (results, error, data) => {
        return [{ type: 'Albums', id: data.id }]
      },
    }),
    createLabelForAlbum: builder.mutation({
      query: ({ album, label }) => {
        return {
          url: `albums/${album.id}/labels`,
          method: 'POST',
          body: label,
        }
      },
      invalidatesTags: (results, error, { album, label }) => {
        return [{ type: 'LabelsForAlbums', id: album.id }]
      },
    }),
    deleteLabelForAlbum: builder.mutation({
      query: ({ album, label }) => {
        return {
          url: `albums/${album.id}/labels/${label.id}`,
          method: 'DELETE',
        }
      },
      invalidatesTags: (results, error, data) => {
        return [{ type: 'LabelsForAlbums', id: data.album.id }]
      },
    }),
  }),
})

export const {
  useGetLabelsForAlbumQuery,
  useGetJobsForAlbumQuery,
  useGetPhotographersForAlbumQuery,
  useCreateAlbumMutation,
  useUpdateAlbumMutation,
  useCreateLabelForAlbumMutation,
  useDeleteLabelForAlbumMutation,
  useDeleteAlbumMutation,
  useGetStoreAlbumsQuery,
} = albumsApi

export default albumsApi
